import type { TimeConfidence } from './astrology.js';
import type { MemoryCategory } from './memory.js';
import type { LocaleId } from './user.js';

/**
 * The grounding contract between the app and Ally.
 *
 * The application calculates chart facts; Ally interprets them. Ally never
 * invents planetary positions, houses, lagna, aspects, dashas, transit dates
 * or match grades — it may only reference the facts supplied here, and each
 * reply cites which facts it used so the UI can show sources.
 */

export interface ChartFact {
  /** Stable id, e.g. "jyotish.moon.nakshatra" or "western.aspect.sun-square-saturn". */
  id: string;
  label: string;
  value: string;
  system: 'jyotish' | 'western' | 'meta';
  confidence: 'exact' | 'range' | 'unknown';
}

export interface AllyMessage {
  role: 'user' | 'ally';
  text: string;
}

export interface MemoryForPrompt {
  category: MemoryCategory;
  content: string;
}

/**
 * Product-level AI tiers. UI copy: standard = "Ask Astrally",
 * deeper = "Go Deeper", master = "Master Reading". Raw provider model names
 * never appear in product surfaces; the mapping lives in remote config /
 * backend env.
 */
export type AiModelClass = 'standard' | 'deeper' | 'master';

/**
 * Ally's two voices.
 *
 * TWO PERSONAS OVER ONE PIPELINE — not two AI stacks. Same providers, same
 * model classes, same entitlement/credit path, same request and response
 * schema, same calculated facts. Only the system prompt and the vocabulary
 * differ. Two independent stacks would double the surface area and the cost
 * for nothing the user can see. The full reasoning lives beside the choice, in
 * functions/src/ai/systemPrompt.ts.
 *
 * 'jyotish' is the DEFAULT because Astrally is an Indian-first product: the
 * Jyotish reading is the spine and Western astrology is an area a user opts
 * into. A turn that says nothing about persona gets the Jyotish astrologer.
 */
export const ALLY_PERSONAS = ['jyotish', 'western'] as const;
export type AllyPersona = (typeof ALLY_PERSONAS)[number];
export const DEFAULT_ALLY_PERSONA: AllyPersona = 'jyotish';

export interface AllyTurnRequest {
  locale: LocaleId;
  timeConfidence: TimeConfidence;
  /** Product tier for this generation. Default 'standard'. */
  modelClass?: AiModelClass;
  /**
   * Which voice answers this turn. Absent or null means 'jyotish'.
   *
   * The persona does NOT change what is calculated — the engine computes both
   * traditions for every user either way, and both fact sets are still sent.
   * The persona selects the vocabulary and which facts lead.
   */
  persona?: AllyPersona;
  /** Client-generated id for idempotent credit charging on paid tiers. */
  generationId?: string;
  /** Trailing conversation context, oldest first. */
  messages: AllyMessage[];
  facts: ChartFact[];
  memories: MemoryForPrompt[];
  /** Optional structured context about a recommendation being discussed. */
  recommendationContext?: {
    candidateDisplayName: string;
    grades: Record<string, string>;
    sharedInterests: string[];
    rationale: string[];
  };
}

export interface MemorySuggestion {
  category: MemoryCategory;
  content: string;
  confidence: number;
  sensitive: boolean;
}

export interface AllyTurnResponse {
  reply: string;
  /** Ids from AllyTurnRequest.facts that this reply relied on. */
  citedFactIds: string[];
  memorySuggestions: MemorySuggestion[];
  provider: 'sarvam' | 'claude' | 'mock';
  refused?: boolean;
  /** Set on paid tiers: credits consumed and balance after this generation. */
  creditCost?: number;
  creditBalance?: number;
}

export type AiProviderId = 'sarvam' | 'claude' | 'mock';
